import { useMemo } from 'react';
import { TrendUp, TrendDown, Minus, Trophy } from '@phosphor-icons/react';
import { useRatingsHistory } from '@/api/ratings';
import type { CadenceTab } from './EloHistoryChart';
import { computeProgression, type ProgressionMetrics } from './progression.utils';

/**
 * ProgressionStats — 3 cards (FIDE / Lichess / chess.com) résumant la
 * progression sur les 30 derniers jours pour la cadence sélectionnée
 * (T5 P1 Stats Hub).
 *
 * Pour chaque plateforme on calcule à partir de l'historique :
 *  - delta 30j (premier vs dernier point dans la fenêtre)
 *  - tendance (slope régression linéaire simple sur les points 30j)
 *  - record all-time (max value + date)
 *
 * Design : pattern aligné `<RatingBadge variant="card">`, palette plateforme,
 * Phosphor only, tabular numerics partout, no emoji.
 */

interface PlatformConfig {
  id: 'fide' | 'lichess' | 'chesscom';
  label: string;
  color: string;
}

const PLATFORMS: ReadonlyArray<PlatformConfig> = [
  { id: 'fide', label: 'FIDE', color: 'var(--paper-50)' },
  { id: 'lichess', label: 'Lichess', color: 'var(--violet-500)' },
  { id: 'chesscom', label: 'chess.com', color: 'var(--amber-500)' },
];

const CADENCE_MAP: Record<CadenceTab, Record<'fide' | 'lichess' | 'chesscom', string | null>> = {
  classique: { fide: 'standard', lichess: 'classical', chesscom: null },
  rapide: { fide: 'rapide', lichess: 'rapid', chesscom: 'rapid' },
  blitz: { fide: 'blitz', lichess: 'blitz', chesscom: 'blitz' },
};

export interface ProgressionStatsProps {
  cadence: CadenceTab;
}

interface ProgressionCardProps {
  platform: PlatformConfig;
  cadenceLabel: string | null;
  metrics: ProgressionMetrics;
  isLoading: boolean;
  isError: boolean;
  available: boolean;
}

function formatDelta(delta: number): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta)}`;
}

function ProgressionCard({
  platform,
  cadenceLabel,
  metrics,
  isLoading,
  isError,
  available,
}: ProgressionCardProps): JSX.Element {
  const trendIcon = (() => {
    if (metrics.trend === 'up')
      return <TrendUp size={14} color="var(--volt-500)" aria-hidden="true" />;
    if (metrics.trend === 'down')
      return <TrendDown size={14} color="var(--coral-500)" aria-hidden="true" />;
    return <Minus size={14} color="var(--ink-300)" aria-hidden="true" />;
  })();

  const trendLabel = (() => {
    if (metrics.trend === 'up') return 'croissante';
    if (metrics.trend === 'down') return 'décroissante';
    if (metrics.trend === 'flat') return 'stable';
    return '—';
  })();

  const deltaColor =
    metrics.delta30d == null
      ? 'var(--ink-300)'
      : metrics.delta30d > 0
        ? 'var(--volt-500)'
        : metrics.delta30d < 0
          ? 'var(--coral-500)'
          : 'var(--ink-300)';

  return (
    <div
      className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-3"
      data-testid={`progression-${platform.id}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="size-2 rounded-full"
          style={{ backgroundColor: platform.color }}
          aria-hidden="true"
        />
        <span
          className="text-caption uppercase"
          style={{ color: platform.color, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {platform.label}
          {cadenceLabel && <span className="text-ink-300 ml-1.5">· {cadenceLabel}</span>}
        </span>
      </div>

      {!available && (
        <p className="text-ink-300 italic text-body-sm">Cadence non disponible sur cette plateforme.</p>
      )}

      {available && isLoading && (
        <p className="text-ink-300 font-mono text-body-sm">Chargement…</p>
      )}

      {available && isError && (
        <p className="text-coral-500 font-mono text-body-sm">Erreur de chargement.</p>
      )}

      {available && !isLoading && !isError && metrics.pointsCount === 0 && (
        <p className="text-ink-300 italic text-body-sm">
          Aucune donnée historique.
        </p>
      )}

      {available && !isLoading && !isError && metrics.pointsCount > 0 && (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-300">
              Delta 30j
            </span>
            <span
              className="font-mono tabular-nums"
              style={{ fontSize: 24, color: deltaColor, lineHeight: 1 }}
              data-testid={`progression-${platform.id}-delta`}
            >
              {metrics.delta30d == null ? '—' : `${formatDelta(metrics.delta30d)} ELO`}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-body-sm font-mono">
            {trendIcon}
            <span className="text-ink-300">Tendance</span>
            <span
              className="text-paper-50"
              data-testid={`progression-${platform.id}-trend`}
            >
              {trendLabel}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-body-sm font-mono border-t border-ink-700 pt-2 mt-auto">
            <Trophy size={14} color="var(--amber-500)" aria-hidden="true" />
            <span className="text-ink-300">Record</span>
            <span
              className="text-paper-50 tabular-nums"
              data-testid={`progression-${platform.id}-record`}
            >
              {metrics.recordValue ?? '—'}
            </span>
            {metrics.recordDate && (
              <span className="text-ink-300 text-[11px] ml-auto tabular-nums">
                {metrics.recordDate}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const CADENCE_LABELS: Record<CadenceTab, string> = {
  classique: 'Classique',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

export function ProgressionStats({ cadence }: ProgressionStatsProps): JSX.Element {
  const fideCad = CADENCE_MAP[cadence].fide;
  const lichessCad = CADENCE_MAP[cadence].lichess;
  const chesscomCad = CADENCE_MAP[cadence].chesscom;

  const fideQ = useRatingsHistory('fide', fideCad ?? 'standard');
  const lichessQ = useRatingsHistory('lichess', lichessCad ?? 'rapid');
  const chesscomQ = useRatingsHistory('chesscom', chesscomCad ?? 'rapid');

  const metrics = useMemo(() => {
    return {
      fide: computeProgression(fideQ.data?.points ?? []),
      lichess: computeProgression(lichessQ.data?.points ?? []),
      chesscom: computeProgression(chesscomQ.data?.points ?? []),
    };
  }, [fideQ.data, lichessQ.data, chesscomQ.data]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="progression-stats">
      <ProgressionCard
        platform={PLATFORMS[0]!}
        cadenceLabel={CADENCE_LABELS[cadence]}
        metrics={metrics.fide}
        isLoading={fideQ.isLoading && fideCad !== null}
        isError={fideQ.isError && fideCad !== null}
        available={fideCad !== null}
      />
      <ProgressionCard
        platform={PLATFORMS[1]!}
        cadenceLabel={CADENCE_LABELS[cadence]}
        metrics={metrics.lichess}
        isLoading={lichessQ.isLoading && lichessCad !== null}
        isError={lichessQ.isError && lichessCad !== null}
        available={lichessCad !== null}
      />
      <ProgressionCard
        platform={PLATFORMS[2]!}
        cadenceLabel={CADENCE_LABELS[cadence]}
        metrics={metrics.chesscom}
        isLoading={chesscomQ.isLoading && chesscomCad !== null}
        isError={chesscomQ.isError && chesscomCad !== null}
        available={chesscomCad !== null}
      />
    </div>
  );
}
